import { Dispatch } from 'redux';
import { EActions } from 'redux/actions/EActions';
import CrudService from 'services/crud.service';
import { All } from 'services/defaults';

export enum ECrudType {
  agreements = 'agreements',
  contacts = 'contacts',
  countries = 'countries',
  dashboard = 'dashboard',
  expenses = 'expenses',
  invoices = 'invoices',
  rates = 'rates',
  shifts = 'shifts',
  states = 'states',
  users = 'users',
  organizationUsers = 'organizationUsers',
}

export enum EActionGetType {
  LIST = 'LIST',
  DROPDOWN = 'DROPDOWN',
}

export class CrudActions<T extends { id?: string }> {
  paramsDrop: any = undefined;
  paramsCrud: any = undefined;
  pageNumber = 1;
  append = false;

  constructor(public type: ECrudType, public service: CrudService<T>) {}

  CleanUp(payload: any = undefined) {
    return { type: this.type + '_' + EActions.CRUD_CLEANUP, payload };
  }

  ToggleFilter() {
    return { type: this.type + '_' + EActions.CRUD_TOGGLE_FILTER };
  }

  BeginLoading() {
    return { type: this.type + '_' + EActions.CRUD_LOADING };
  }

  EndEdit() {
    return { type: this.type + '_' + EActions.CRUD_END_EDIT };
  }

  BeginEditById(id: string, override?: any) {
    return async (dispatch: Dispatch) => {
      const payload = await this.service.GetOne(id);
      dispatch({
        type: this.type + '_' + EActions.CRUD_BEGIN_EDIT,
        payload: { ...payload, ...override },
      });
    };
  }

  BeginEdit(obj: T | undefined) {
    return {
      type: this.type + '_' + EActions.CRUD_BEGIN_EDIT,
      payload: obj,
    };
  }

  GetRecipients(id: string) {
    return async (dispatch: Dispatch) => {
      try {
        const result = await this.service.GetRecipients(id);
        dispatch(this.GetRecipientsSuccess(result));
      } catch (error) {
        dispatch(this.GetRecipientsFail(error as any));
      }
    };
  }

  GetRecipientsSuccess(recipients: any) {
    return {
      type: this.type + '_' + EActions.GET_RECIPIENTS_SUCCESS,
      payload: recipients,
    };
  }

  GetRecipientsFail(error: string) {
    return {
      type: this.type + '_' + EActions.GET_RECIPIENTS_FAIL,
      payload: error,
    };
  }

  GetOne(id: string) {
    return async (dispatch: Dispatch) => {
      try {
        const result = await this.service.GetOne(id);
        dispatch(this.GetOneSuccess(result));
      } catch (error) {
        dispatch(this.GetOneFail(error as any));
      }
    };
  }

  GetOneSuccess(one: any) {
    return {
      type: this.type + '_' + EActions.GET_ONE_SUCCESS,
      payload: one,
    };
  }

  GetOneFail(error: string) {
    return {
      type: this.type + '_' + EActions.GET_ONE_FAIL,
      payload: error,
    };
  }

  GetDropdown(params: any = undefined) {
    return async (dispatch: Dispatch) => {
      try {
        if (typeof params !== 'undefined') this.paramsDrop = params;
        const result = await this.service.GetDropdown(this.paramsDrop);
        dispatch(this.GetSuccess(result, EActionGetType.DROPDOWN, false));
      } catch (error) {
        dispatch(this.GetFail(error as any));
      }
    };
  }

  GetCrud(pageNumber: number | undefined = undefined, append: boolean | undefined = undefined) {
    return async (dispatch: Dispatch) => {
      dispatch({ type: this.type + '_' + EActions.CRUD_GET });
      try {
        if (typeof pageNumber !== 'undefined') this.pageNumber = pageNumber;
        if (typeof append !== 'undefined') this.append = append;
        const result = await this.service.GetAll(this.pageNumber || 1, this.paramsCrud);
        dispatch(this.GetSuccess(result, EActionGetType.LIST, this.append));
      } catch (error) {
        dispatch(this.GetFail(error as any));
      }
    };
  }

  Reload() {
    return async (dispatch: Dispatch) => {
      dispatch({ type: this.type + '_' + EActions.CRUD_GET });
      try {
        const result = await this.service.Reload(this.paramsCrud, this.pageNumber);
        dispatch(this.GetSuccess(result, EActionGetType.LIST, false));
      } catch (error) {
        dispatch(this.GetFail(error as any));
      }
    };
  }

  GetSuccess(lst: All<T>, type: EActionGetType = EActionGetType.LIST, append: boolean) {
    return {
      type: this.type + '_' + EActions.CRUD_GET_SUCCESS + '_' + type,
      payload: lst,
      append,
    };
  }

  GetFail(error: string) {
    return {
      type: this.type + '_' + EActions.CRUD_GET_FAIL,
      payload: error,
    };
  }

  CleanUpNotify() {
    return {
      type: this.type + '_' + EActions.NOTIFY_CLEANUP,
    };
  }

  Notify(id: any, recipients: any) {
    return async (dispatch: Dispatch) => {
      dispatch({ type: this.type + '_' + EActions.NOTIFY });
      try {
        await this.service.Notify(id, { recipients } as any);
        dispatch(this.NotifySuccess());
      } catch (error) {
        dispatch(this.NotifyFail(error as any));
      }
    };
  }

  NotifySuccess() {
    return {
      type: this.type + '_' + EActions.NOTIFY_SUCCESS,
    };
  }

  NotifyFail(error: string) {
    return {
      type: this.type + '_' + EActions.NOTIFY_FAIL,
      payload: error,
    };
  }

  Save(values: any, dropdown = false, notify = true) {
    return async (dispatch: Dispatch) => {
      dispatch({ type: this.type + '_' + EActions.CRUD_SAVE });
      try {
        const result = values.id ? await this.service.Update(values.id, values, notify) : await this.service.Create(values, notify);
        dispatch(this.SaveSuccess(result));
        dispatch(this.EndEdit());
        if (values.id) {
          dispatch({ type: this.type + '_' + EActions.CRUD_REPLACE, payload: result });
        } else if (this.append) {
          // on mobile we append the result
          this.Reload()(dispatch);
        } else {
          if (dropdown) this.GetDropdown()(dispatch);
          else this.GetCrud()(dispatch);
        }
        return result;
      } catch (error) {
        dispatch(this.SaveFail(error as any));
      }
    };
  }

  SaveSuccess(obj: T) {
    return {
      type: this.type + '_' + EActions.CRUD_SAVE_SUCCESS,
      payload: obj,
    };
  }

  SaveFail(error: string) {
    return {
      type: this.type + '_' + EActions.CRUD_SAVE_FAIL,
      payload: error,
    };
  }

  Delete(id: string) {
    return async (dispatch: Dispatch) => {
      dispatch({ type: this.type + '_' + EActions.CRUD_DELETE });
      try {
        await this.service.Delete(id);
        this.Reload()(dispatch);
      } catch (error) {
        dispatch(this.DeleteFail(error as any));
      }
    };
  }

  DeleteFail(error: string) {
    return {
      type: this.type + '_' + EActions.CRUD_DELETE_FAIL,
      payload: error,
    };
  }
}
